import { RENDER_DIVISORS, renderChange, type RenderChange, type RenderDivisor, type Size } from "odyssey-shared";
import { say } from "../../words.ts";
import { parseRenderDivisor } from "../display-settings.ts";

/**
 * Render resolution as Settings offers it (ADR-093 decisions 10 and 12): Full, Half and Third, an option the
 * window does not offer disabled, and the row's note. The note says what a pending choice would cost against the
 * divisor in force, in tiles and in the HUD's scale, worked out from the window and never written down; with
 * nothing to lose it says why an option is disabled, or nothing. A choice's value is its divisor as `settings.ts`
 * keeps it in the store. Every word is a draft for the author.
 */

/**
 * A choice's value: its divisor as the store keeps it.
 */
export type RenderChoiceValue = "1" | "2" | "3";

/**
 * The choices' labels, by divisor.
 */
export const RENDER_LABELS: Readonly<Record<RenderDivisor, string>> = { 1: "Full", 2: "Half", 3: "Third" };

/**
 * How the sentence for a disabled option names its divisor.
 */
const NOT_OFFERED_WORDS: Readonly<Record<RenderDivisor, string>> = { 1: say("menu.render.full"), 2: say("menu.render.half"), 3: say("menu.render.third") };

/**
 * A divisor as a choice's value.
 *
 * @param divisor The divisor.
 * @returns The value, the divisor as the store keeps it.
 */
export function renderChoiceValue(divisor: RenderDivisor): RenderChoiceValue {
  return String(divisor) as RenderChoiceValue;
}

/**
 * A choice's value as its divisor.
 *
 * @param value The value.
 * @returns The divisor, read as the store's text is read.
 */
export function renderChoiceDivisor(value: RenderChoiceValue): RenderDivisor {
  return parseRenderDivisor(value);
}

/**
 * The choices as the segmented choice takes them, in the order Full, Half, Third.
 *
 * @param offered The divisors the window offers.
 * @returns Each choice's value and label, disabled when the window does not offer it.
 * @example
 * renderOptions([1, 2]).map((option) => option.disabled);
 * // [false, false, true]
 */
export function renderOptions(offered: readonly RenderDivisor[]): readonly { readonly value: RenderChoiceValue; readonly label: string; readonly disabled: boolean }[] {
  return RENDER_DIVISORS.map((divisor) => ({ value: renderChoiceValue(divisor), label: RENDER_LABELS[divisor], disabled: !offered.includes(divisor) }));
}

/**
 * A view in tiles as words: whole tiles, or one decimal place when whole tiles would read the same before and
 * after.
 *
 * @param view The view in tiles.
 * @param places Decimal places, 0 or 1.
 * @returns The view, such as "20 by 11".
 */
function tilesWords(view: Size, places: number): string {
  return say("menu.render.tilesBy", { width: view.width.toFixed(places), height: view.height.toFixed(places) });
}

/**
 * What a change of divisor costs, in sentences.
 *
 * @param change The losses, as `renderChange` gives them.
 * @returns One sentence a loss, the view first, joined by a space; null when nothing is lost.
 * @example
 * renderCostSentence(renderChange({ width: 2560, height: 1440 }, 1, 2));
 * // "Shows less of the world: 20 by 11 tiles instead of 27 by 15."
 */
export function renderCostSentence(change: RenderChange): string | null {
  const sentences: string[] = [];
  if (change.world !== null) {
    const places = tilesWords(change.world.from, 0) === tilesWords(change.world.to, 0) ? 1 : 0;
    sentences.push(say("menu.render.lessWorld", { to: tilesWords(change.world.to, places), from: tilesWords(change.world.from, places) }));
  }
  if (change.hud !== null) {
    sentences.push(say("menu.render.smallerHud", { to: String(change.hud.to), from: String(change.hud.from) }));
  }
  return sentences.length === 0 ? null : sentences.join(" ");
}

/**
 * Why the window offers no more: the sentence for the smallest divisor it does not offer.
 *
 * @param offered The divisors the window offers.
 * @returns The sentence, or null when every divisor is offered.
 * @example
 * renderOfferSentence([1]);
 * // "This screen has too few pixels to draw at half."
 */
export function renderOfferSentence(offered: readonly RenderDivisor[]): string | null {
  const missing = RENDER_DIVISORS.find((divisor) => !offered.includes(divisor));
  return missing === undefined ? null : say("menu.render.tooFewPixels", { divisor: NOT_OFFERED_WORDS[missing] });
}

/**
 * What a pending choice would cost against the divisor in force on a window.
 *
 * @param window The window's device size and the divisors it offers.
 * @param applied The divisor in force.
 * @param pending The divisor chosen.
 * @param step The pending world zoom step, which both views are measured at.
 * @returns The cost's sentences, or null when the choice is the one in force or loses nothing.
 */
export function renderCost(window: { readonly device: Size }, applied: RenderDivisor, pending: RenderDivisor, step: number): string | null {
  return applied === pending ? null : renderCostSentence(renderChange(window.device, applied, pending, step));
}

/**
 * The row's note: what the pending choice would cost, else why an option is disabled, else nothing.
 *
 * @param window The window's device size and the divisors it offers.
 * @param applied The divisor in force.
 * @param pending The divisor chosen.
 * @param step The pending world zoom step.
 * @returns The note, or null for a row with none.
 */
export function renderNote(window: { readonly device: Size; readonly offered: readonly RenderDivisor[] }, applied: RenderDivisor, pending: RenderDivisor, step: number): string | null {
  return renderCost(window, applied, pending, step) ?? renderOfferSentence(window.offered);
}
